import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["data-test", "aria-label"]
const _hoisted_2 = ["src", "alt"]
const _hoisted_3 = {
  key: 1,
  class: "user-avatar__initials",
  "aria-hidden": "true"
}

import { computed, ref, watch } from 'vue';
import { getSafeAvatarUrl, DEFAULT_USER_AVATAR } from '@/shared/lib/avatar';

/**
 * Допустимые размеры. Жёсткий enum, чтобы не плодились случайные значения
 * (size="37" и т.п.) — все вызывающие должны лечь на дизайн-систему.
 */
export type UserAvatarSize = 32 | 48 | 64 | 96 | 128;

interface UserLike {
    id?: string | number | null;
    first_name?: string | null;
    last_name?: string | null;
    /**
     * Иногда профиль приходит с уже собранным полным именем (например, из
     * mentor list endpoint). Используется как fallback, когда нет
     * first_name/last_name отдельно.
     */
    full_name?: string | null;
    /**
     * Иногда (registrations, attendees) на бэке поле просто `name`.
     */
    name?: string | null;
    avatar_url?: string | null;
    /**
     * Альтернативное имя поля для аватара (в mentor-list endpoint поле
     * называется mentor_avatar_url).
     */
    mentor_avatar_url?: string | null;
}

const BRAND_AVATAR_GRADIENT = 'linear-gradient(135deg, #7B65F7 0%, #9F8DFF 100%)';
const NEUTRAL_AVATAR_BG = '#94A3B8'; // slate-400 — для совсем «безымянных» аватаров

/**
 * Нормализация имени → 1-2 заглавные буквы инициалов.
 *
 * Источники имени, по приоритету:
 *   1. props.displayName (override)
 *   2. user.first_name + user.last_name
 *   3. user.full_name (склеенное на бэке имя)
 *   4. user.name (поле из attendees/registrations)
 *
 * Возвращает '' если имя пустое — это сигнал родителю рендерить
 * иконку-фолбэк вместо инициалов.
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'UserAvatar',
  props: {
    user: { default: null },
    avatarUrl: { default: null },
    displayName: { default: null },
    size: { default: 48 },
    dataTest: { default: '' },
    alt: { default: '' }
  },
  setup(__props: any) {

/**
 * UserAvatar — единая точка отрисовки аватара пользователя/наставника.
 *
 * Эта обёртка убирает разнобой между десятком разных мест, где у нас
 * раньше рисовался один и тот же блок: «есть avatar_url — <img>, нет —
 * что-нибудь страшное». Самым страшным был дефолтный
 * /default-avatar.svg (серый кружок с белой фигуркой человека) и
 * Vuetify'шный mdi-account-circle — оба читаются как «удалённый
 * аккаунт» и портили листинги/наставнические карточки/календарь.
 *
 * Стратегия фолбэка, по приоритету:
 *   1. avatar_url присутствует → <img>. URL предварительно проходит
 *      через getSafeAvatarUrl(), который ловит легаси-плейсхолдеры
 *      (testbucketplatforma34/blank.jpg и т.п.) и апскейлит
 *      Google-аватарки (см. shared/lib/avatar.ts).
 *   2. avatar отсутствует, но известно имя → круг с инициалами на
 *      фоновом цвете, выбранном детерминированно по hash(user.id).
 *      Тот же пользователь всегда получает тот же цвет — это даёт
 *      визуальную узнаваемость между сессиями.
 *   3. ни avatar, ни имени → нейтральный серый круг с
 *      mdi-account-outline (НЕ filled mdi-account-circle, который и
 *      был источником жалобы про «ублюдскую фотографию»).
 *
 * Размеры: 32 / 48 / 64 / 96 / 128 — соответствуют существующим UI
 * масштабам (мини-чип в чате, attendee-row, карточка эксперта, hero,
 * крупный preview в setup).
 */
const props = __props;

/**
 * Фолбэк-фон — единый фирменный фиолетовый градиент.
 *
 * Альтернатива (палитра из 8 цветов по hash(user.id)) даёт визуальную
 * узнаваемость, но на практике превращается в «новогоднюю ёлку»: один
 * и тот же пользователь видит оранжевую аватарку в settings и зелёную
 * в header (разные id-источники), а каталог наставников выглядит как
 * радуга случайных кружков.
 *
 * Решение от продукта — единый бренд-градиент для всех аватаров
 * вместо palette+hash. Белые инициалы поверх 135°-фиолетового
 * (#7B65F7 → #9F8DFF) совпадают с CTA-кнопкой «Создать сессию» и
 * шапкой Share-блока, узнавание идёт через цвет бренда, а не
 * случайный hash.
 *
 * Дефолтный grey остаётся как нейтральный фолбэк, когда инициалов
 * посчитать не из чего (мы вообще не знаем, кто это).
 */
const initials = computed<string>(() => {
    const explicit = (props.displayName ?? '').trim();
    if (explicit) {
        return computeInitialsFromName(explicit);
    }

    const u = props.user;
    if (u) {
        const first = (u.first_name ?? '').trim();
        const last = (u.last_name ?? '').trim();
        if (first || last) {
            const firstInitial = first.charAt(0);
            const lastInitial = last.charAt(0);
            return (firstInitial + lastInitial).toUpperCase();
        }
        const full = (u.full_name ?? u.name ?? '').trim();
        if (full) {
            return computeInitialsFromName(full);
        }
    }

    return '';
});

function computeInitialsFromName(name: string): string {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.charAt(0) ?? '';
    const second = parts[1]?.charAt(0) ?? '';
    return (first + second).toUpperCase();
}

/**
 * URL аватара после прохождения через safety/upscale helper. Empty или
 * default-avatar.svg означают «нет реального аватара» — рисуем фолбэк.
 *
 * Также трекаем onerror-fallback: если <img> упал (битый URL, 404 на
 * S3 и т.п.), переключаемся на инициалы.
 */
const imageBroken = ref<boolean>(false);

const rawAvatarUrl = computed<string>(() => {
    if (props.avatarUrl != null) {
        return props.avatarUrl ?? '';
    }
    return props.user?.avatar_url ?? props.user?.mentor_avatar_url ?? '';
});

watch(rawAvatarUrl, () => {
    // Сбрасываем флаг при смене URL — может быть валидным.
    imageBroken.value = false;
});

const resolvedAvatarUrl = computed<string>(() => {
    const raw = rawAvatarUrl.value;
    if (!raw) return '';
    return getSafeAvatarUrl(raw);
});

const hasAvatar = computed<boolean>(() => {
    if (imageBroken.value) return false;
    const resolved = resolvedAvatarUrl.value;
    // getSafeAvatarUrl() возвращает DEFAULT_USER_AVATAR для пустых/легаси,
    // а наш фолбэк красивее дефолтной картинки — поэтому отбрасываем.
    if (!resolved || resolved === DEFAULT_USER_AVATAR) return false;
    return true;
});

const handleImageError = (): void => {
    imageBroken.value = true;
};

/**
 * Фон фолбэка:
 *   • если есть инициалы → бренд-градиент (`BRAND_AVATAR_GRADIENT`),
 *     один на все аватары — никаких разноцветных кружков, узнаём
 *     пользователя по инициалам, а не по случайному цвету;
 *   • если инициалов нет (вообще не знаем, кто) → нейтральный
 *     slate-400, чтобы заглушка не «кричала».
 *
 * Возвращаем строку CSS-значения (background-image / background-color),
 * `fallbackStyle` ниже сам решит, какое именно свойство задать.
 */
const fallbackBackground = computed<string>(() => {
    if (!initials.value) return NEUTRAL_AVATAR_BG;
    return BRAND_AVATAR_GRADIENT;
});

const fallbackClass = computed<string>(() => {
    if (hasAvatar.value) return 'user-avatar--has-img';
    if (initials.value) return 'user-avatar--has-initials';
    return 'user-avatar--has-icon';
});

const fallbackStyle = computed<Record<string, string>>(() => {
    if (hasAvatar.value) return {};
    if (initials.value) {
        // Градиент кладём в background (shorthand), чтобы не
        // конкурировать с возможным background-color из родительских
        // стилей и точно перебить дефолтный фон .user-avatar.
        return { background: fallbackBackground.value };
    }
    return { backgroundColor: fallbackBackground.value };
});

/**
 * Размер v-icon внутри иконочного фолбэка. Берём ~55% от размера
 * круга — это визуально центрирует иконку без вылета за края.
 */
const iconSize = computed<number>(() => Math.round(props.size * 0.55));

const ariaLabel = computed<string>(() => {
    if (props.alt) return props.alt;
    const u = props.user;
    const namePieces = [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim();
    if (namePieces) return namePieces;
    if (u?.full_name) return u.full_name;
    if (u?.name) return u.name;
    if (props.displayName) return props.displayName;
    return 'Аватар пользователя';
});

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["user-avatar", [`user-avatar--size-${_ctx.size}`, fallbackClass.value]]),
    style: _normalizeStyle(fallbackStyle.value),
    "data-test": _ctx.dataTest || 'user-avatar',
    "aria-label": ariaLabel.value,
    role: "img"
  }, [
    (hasAvatar.value)
      ? (_openBlock(), _createElementBlock("img", {
          key: 0,
          src: resolvedAvatarUrl.value,
          alt: ariaLabel.value,
          class: "user-avatar__img",
          onError: handleImageError
        }, null, 40, _hoisted_2))
      : (initials.value)
        ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(initials.value), 1))
        : (_openBlock(), _createBlock(_component_v_icon, {
            key: 2,
            class: "user-avatar__icon",
            size: iconSize.value,
            "aria-hidden": "true"
          }, {
            default: _withCtx(() => _cache[0] || (_cache[0] = [
              _createTextVNode("mdi-account-outline")
            ])),
            _: 1
          }, 8, ["size"]))
  ], 14, _hoisted_1))
}
}

})